import { useState } from "react";
import {
  HelpCircle, Database, ShoppingCart, FileSpreadsheet, Bot,
  Calendar, TrendingUp, ClipboardList, Settings, Play,
  Download, Zap, Shield, BookOpen, RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const MODULE_GUIDES = [
  {
    title: "Materials Dashboard",
    icon: Database,
    color: "text-blue-600 bg-blue-100",
    path: "/",
    tips: [
      "Search by material number or description",
      "Filter by type (RAW, FERT, SEMI) and plant",
      "Click a material card for full details",
      "Data refreshes from SAP on each page load",
    ],
  },
  {
    title: "Sales Orders",
    icon: ShoppingCart,
    color: "text-emerald-600 bg-emerald-100",
    path: "/sales-orders",
    tips: [
      "Filter by customer, plant, or status",
      "Sort by clicking any column header",
      "KPI cards show totals at the top",
      "Export the full list to Excel",
    ],
  },
  {
    title: "Planning Report",
    icon: FileSpreadsheet,
    color: "text-amber-600 bg-amber-100",
    path: "/production-planning",
    tips: [
      "Combines Sales Orders + Business Partners + Production Orders",
      "See which items already have production orders",
      "Track committed vs. requested delivery dates",
      "Export to Excel for offline analysis",
    ],
  },
  {
    title: "AI Planning Agent",
    icon: Bot,
    color: "text-violet-600 bg-violet-100",
    path: "/planning-agent",
    tips: [
      "Ask in plain English: \"Explode BOM for FG1_CP at qty 100\"",
      "Check availability: \"What components are short for FG2_CP?\"",
      "Forecast demand: \"Forecast FG1_CP for 3 months\"",
      "Conversations are saved as sessions you can revisit",
    ],
  },
  {
    title: "Scheduling",
    icon: Calendar,
    color: "text-cyan-600 bg-cyan-100",
    path: "/scheduling",
    tips: [
      "Click 'Generate Schedule' to create from open sales orders",
      "Toggle Critical Path to highlight bottlenecks",
      "Use What-If panel to model rush orders or machine downtime",
      "Create production orders directly from the Orders tab",
      "Batch-select orders for bulk PO creation",
    ],
  },
  {
    title: "Demand Forecasting",
    icon: TrendingUp,
    color: "text-pink-600 bg-pink-100",
    path: "/forecasting",
    tips: [
      "Select a material or material group to forecast",
      "Set horizon (1-12 months) for projections",
      "AI provides trend analysis and recommendations",
      "Export forecast data to Excel",
    ],
  },
  {
    title: "PO Tracker",
    icon: ClipboardList,
    color: "text-indigo-600 bg-indigo-100",
    path: "/production-order-tracker",
    tips: [
      "KPI cards show order counts by status",
      "Search by PO number, material, or sales order",
      "Release individual orders or batch-release selected",
      "Committed dates from sales orders shown in green",
    ],
  },
  {
    title: "Settings (Admin)",
    icon: Settings,
    color: "text-zinc-600 bg-zinc-100",
    path: "/settings",
    tips: [
      "Only one SAP connection can be active at a time",
      "Use 'Credentials' to update SAP password without recreating",
      "Test connection before going live",
      "Use the separate admin pages for users, AI, organizations, and audit history",
    ],
    adminOnly: true,
  },
];

const QUICK_TIPS = [
  { icon: Zap, text: "All data is live from SAP — changes in SAP reflect immediately" },
  { icon: Download, text: "Every report page has an 'Export Excel' button for offline use" },
  { icon: Shield, text: "SAP credentials are encrypted at rest with AES encryption" },
  { icon: Play, text: "Use the AI Agent for natural language queries about your production data" },
  { icon: RotateCcw, text: "Click 'Refresh' on any page to pull the latest data from SAP" },
];

const HelpPanel = ({ isAdmin = false, onRestartTour }) => {
  const [open, setOpen] = useState(false);
  const guides = MODULE_GUIDES.filter((g) => !g.adminOnly || isAdmin);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative w-8 h-8"
          data-testid="help-button"
        >
          <HelpCircle className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[420px] sm:max-w-[420px] p-0" data-testid="help-panel">
        <SheetHeader className="p-6 pb-4 border-b">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-primary" />
            Help Center
          </SheetTitle>
          <SheetDescription>
            Quick guides, tips, and feature walkthroughs
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="guides" className="flex flex-col h-[calc(100vh-140px)]">
          <TabsList className="mx-6 mt-4 grid grid-cols-2">
            <TabsTrigger value="guides" data-testid="help-tab-guides">Module Guides</TabsTrigger>
            <TabsTrigger value="tips" data-testid="help-tab-tips">Quick Tips</TabsTrigger>
          </TabsList>

          <TabsContent value="guides" className="flex-1 m-0">
            <ScrollArea className="h-[calc(100vh-260px)]">
              <div className="p-6 space-y-4">
                {guides.map((mod) => {
                  const Icon = mod.icon;
                  return (
                    <div key={mod.path} className="border rounded-lg p-4 hover:bg-secondary/40 transition-colors">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${mod.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <h4 className="font-medium text-sm">{mod.title}</h4>
                        {mod.adminOnly && <Badge variant="outline" className="text-[10px] h-5">Admin</Badge>}
                      </div>
                      <ul className="space-y-1.5 ml-11">
                        {mod.tips.map((tip, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                            <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="tips" className="flex-1 m-0">
            <ScrollArea className="h-[calc(100vh-260px)]">
              <div className="p-6 space-y-3">
                {QUICK_TIPS.map((tip, i) => {
                  const Icon = tip.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/40">
                      <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <p className="text-sm text-muted-foreground">{tip.text}</p>
                    </div>
                  );
                })}

                <div className="pt-4 border-t mt-6">
                  <h4 className="font-medium text-sm mb-2">Keyboard Tips</h4>
                  <div className="space-y-2">
                    {[
                      ["Search", "Click search field and start typing"],
                      ["Sort", "Click any table column header"],
                      ["Export", "Look for the 'Export Excel' button in each page header"],
                    ].map(([action, desc]) => (
                      <div key={action} className="flex items-center gap-3 text-xs">
                        <Badge variant="secondary" className="font-mono text-[10px] px-2">{action}</Badge>
                        <span className="text-muted-foreground">{desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Restart Tour Button */}
          <div className="p-4 border-t">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { setOpen(false); onRestartTour?.(); }}
              data-testid="restart-tour-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restart Guided Tour
            </Button>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default HelpPanel;
